import React, { useEffect, useState, useMemo } from "react";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, CardBody, CardHeader, Modal, Form, InputGroup } from "react-bootstrap";
import { faPlus, faSpinner, faSave, faTimes, faSearch } from "@fortawesome/free-solid-svg-icons";
import {
  useCreateTypeMutation,
  useDeleteTypeMutation,
  CustomTypeResponse,
} from "../../../redux/services/CustomTypeService";
import { useFirebaseCustomMenuItemTypes } from "../../../hooks/useFirebaseMenu";
import DataTable, { TableColumn } from "react-data-table-component";
import ButtonComponent from "../../../components/ButtonComponent";
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import ControlledCheckBoxField from "../../../components/ControlledCheckBoxField";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Col, Row, Input, InputGroupText } from "reactstrap";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import ErrorMessageComponent from "../../../components/ErrorMessageComponent";
import Swal from "sweetalert2";
import ViewButtonComponent from "../../../components/ViewButtonComponent";
import DeleteButtonComponent from "../../../components/DeleteButtonComponent";
import EditButtonComponent from "../../../components/EditButtonComponent";

const CustomizeMenuType = () => {
  const navigate = useNavigate();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const { data: typesData, loading: isLoading, error } = useFirebaseCustomMenuItemTypes(restaurantId);
  
  const initialMenuTypes = useMemo(() => typesData?.customMenuItemTypes || [], [typesData]);
  const isError = !!error;

  const [deleteType] = useDeleteTypeMutation();

  const [deletingTypeName, setDeletingTypeName] = useState<string | null>(null);
  const [menuTypes, setMenuTypes] = useState<CustomTypeResponse[]>([]);
  const [editingType, setEditingType] = useState<CustomTypeResponse | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleViewDimensions = (menuType: CustomTypeResponse) => {
    navigate(`/productCatalogue/product-groups/${menuType.name}/dimensions`);
  };

  useEffect(() => {
    if (initialMenuTypes) {
      setMenuTypes(initialMenuTypes);
    }
  }, [initialMenuTypes]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      displayName: "",
      hasMultipleDimensions: false
    }
  });

  // Edit form
  const editForm = useForm<{
    name: string;
    hasMultipleDimensions: boolean;
  }>();

  // Watch the input value to enable/disable the Add button
  const displayNameWatcher = watch("displayName");

  const handleDeleteClick = (type: CustomTypeResponse) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingTypeName(type.name);
        try {
          await deleteType({ restaurantId, customMenuType: type.name, customMenuItemTypeId: type.id }).unwrap();
          toast.success("Your menu item type has been deleted.");
          setMenuTypes(menuTypes.filter((t) => t.name !== type.name));
        } catch (error: any) {
          console.error("Delete Error:", error);
          toast.error(error.data || "Error deleting the menu type.");
        } finally {
          setDeletingTypeName(null);
        }
      }
    });
  };

  const columns: TableColumn<CustomTypeResponse>[] = [
    {
      name: <h6 className="fw-bold">Name</h6>,
      selector: (row) => row.name,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span className="me-2">{row.name}</span>
        </div>
      ),
    },
    {
      name: <h6 className="fw-bold">Total Count</h6>,
      selector: (row) => row.totalCount,
    },
    {
      name: <h6 className="fw-bold">Available Count</h6>,
      selector: (row) => row.availableCount,
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row) => (
        <div className="menuitem-action-buttons d-flex gap-2">
          <ViewButtonComponent
            onClick={() => handleViewDimensions(row)}
            title="View Dimensions"
          />
          <EditButtonComponent
            onClick={() => handleEditClick(row)}
          />
          {deletingTypeName === row.name ? (
            <Button variant="light" size="sm" disabled={true}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <DeleteButtonComponent
              onClick={() => handleDeleteClick(row)}
              title="Delete"
            />
          )}
        </div>
      ),
    },
  ];

  const [createType, { isLoading: isCreationLoading }] =
    useCreateTypeMutation();

  const onSubmit = async (data: any) => {
    try {
      const response = await createType({
        restaurantId: restaurantId,
        name: data.displayName,
        hasMultipleDimensions: data.hasMultipleDimensions || false,
      }).unwrap();
      setMenuTypes([
        ...menuTypes,
        {
          id: response?.id,
          name: data.displayName,
          hasMultipleDimensions: data.hasMultipleDimensions || false,
          createdAt: response?.createdAt || new Date().toISOString(),
          availableCount: response?.availableCount || 0,
          totalCount: response?.totalCount || 0,
        },
      ]);
      reset();
      toast.success("Custom menu type created");
      navigate("/productCatalogue/product-groups");
    } catch (error: any) {
      if (error.status === 409) {
        toast.error(error.data);
        return;
      }
      console.error("Error creating menu type:", error);
      toast.error("Something went wrong");
    }
  };

  const handleEditClick = (row: CustomTypeResponse) => {
    setEditingType(row);
    editForm.setValue("name", row.name);
    editForm.setValue("hasMultipleDimensions", row.hasMultipleDimensions || false);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (data: any) => {
    setIsUpdating(true);
    try {
      await createType({
        restaurantId: restaurantId,
        name: data.name,
        hasMultipleDimensions: data.hasMultipleDimensions || false,
        customMenuItemTypeId: editingType?.id,
      }).unwrap();
      const updatedMenuTypes = menuTypes.map(type =>
        type.id === editingType?.id
          ? {
            ...type,
            name: data.name,
            hasMultipleDimensions: data.hasMultipleDimensions || false
          }
          : type
      );
      setMenuTypes(updatedMenuTypes);
      setShowEditModal(false);
      setEditingType(null);
      editForm.reset();
      toast.success("Menu type updated successfully");
    } catch (error: any) {
      console.error("Error updating menu type:", error);
      toast.error("Failed to update menu type");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingType(null);
    editForm.reset();
  };

  const handleViewDetails = (row: CustomTypeResponse) => {
    navigate(`/productCatalogue/product-groups/${row.name}/dimensions`);
  };

  // Filter menu types based on search query (flexible search - words can be in any order)
  const filteredMenuTypes = useMemo(() => {
    if (!searchQuery.trim()) {
      return menuTypes;
    }
    const searchWords = searchQuery.toLowerCase().trim().split(/\s+/);
    return menuTypes.filter(type => {
      const typeName = type.name.toLowerCase();
      return searchWords.every(word => typeName.includes(word));
    });
  }, [menuTypes, searchQuery]);

  if (isError) {
    return <ErrorMessageComponent />;
  }

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <Card className="mt-3">
        <CardHeader className="header-bg-color">
          <h5>Customize Menu Type</h5>
        </CardHeader>
        <CardBody>
          <form
            className="customize-type-form mt-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Row className="d-flex align-items-center justify-content-between">
              <Col lg="6">
                <ControlledRowTextField
                  label={"Custom Menu Type Name"}
                  inputName={"displayName"}
                  inputType={"text"}
                  placeholder={"Enter Type Name"}
                  control={control}
                  rules={{
                    required: "Type is required",
                    // pattern: {
                    //   value: /^[A-Z0-9_]+$/,
                    //   message:
                    //     "Only capital letters, numbers, and underscores are allowed",
                    // },
                  }}
                />
                {errors.displayName && (
                  <span className="text-danger small">
                    {errors.displayName.message as string}
                  </span>
                )}
              </Col>
              <Col lg="3">
                <ControlledCheckBoxField
                  control={control}
                  inputName="hasMultipleDimensions"
                  label="Has Multiple Dimensions"
                />
              </Col>
              <Col lg="2">
                {isCreationLoading ? (
                  <ButtonComponent
                    type={"submit"}
                    icon={<FontAwesomeIcon icon={faSpinner} spin />}
                    titel="Adding..."
                    isDisabled={true}
                  />
                ) : (
                  <ButtonComponent
                    type="submit"
                    icon={
                      <FontAwesomeIcon
                        icon={faPlus}
                        title={"Add Customizable Type"}
                      />
                    }
                    titel="Add"
                    isDisabled={!displayNameWatcher}
                  />
                )}
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>

      <Card className="mt-3">
        <CardBody>
          {isLoading ? (
            <div>
              <TableLoadingComponent numColumns={4} />
            </div>
          ) : (
            <>
              {/* Search Input */}
              <div className="mb-3">
                <InputGroup>
                  <InputGroupText>
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search menu types by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </div>

              <DataTable
                columns={columns}
                data={filteredMenuTypes}
                onRowClicked={handleViewDetails}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                noDataComponent={
                  <div className="py-4 text-center">
                    {searchQuery ? `No menu types found for "${searchQuery}"` : "No menu types available"}
                  </div>
                }
              />
            </>
          )}
        </CardBody>
      </Card>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={handleEditCancel} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Menu Type Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={editForm.handleSubmit(handleEditSubmit)}>
            <Row className="d-flex align-items-center justify-content-between">
              <Col md={6}>
                <ControlledRowTextField
                  label="Type Name"
                  inputName="name"
                  inputType="text"
                  placeholder="Enter menu type name"
                  control={editForm.control}
                  rules={{
                    required: "Type name is required",
                    minLength: {
                      value: 2,
                      message: "Type name must be at least 2 characters",
                    },
                    maxLength: {
                      value: 50,
                      message: "Type name must be less than 50 characters",
                    },
                  }}
                />
                {editForm.formState.errors.name && (
                  <span className="text-danger small">
                    {editForm.formState.errors.name.message}
                  </span>
                )}
              </Col>
              <Col md={6}>
                <ControlledCheckBoxField
                  control={editForm.control}
                  inputName="hasMultipleDimensions"
                  label="Has Multiple Dimensions"
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleEditCancel} disabled={isUpdating}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancel
          </Button>
          {isUpdating ? (
            <ButtonComponent
              type="button"
              titel="Updating..."
              icon={<FontAwesomeIcon icon={faSpinner} spin />}
              isDisabled={true}
            />
          ) : (
            <ButtonComponent
              type="button"
              onClick={editForm.handleSubmit(handleEditSubmit)}
              titel="Update Name"
              icon={<FontAwesomeIcon icon={faSave} />}
              isDisabled={!editForm.watch("name")}
            />
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomizeMenuType;
